import { IEditorV3 } from "@azu-business/editor-v3";
import { loadWrapper } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import { SignalRContext } from "contexts/SignalRProvider";
import { TlfContext } from "contexts/TlfContext";
import { TocContext } from "contexts/TocContext";
import { SET_HIERARCHY_COMMENT_PANEL, UPDATE_HIERARCHY } from "contexts/hierarchy/interface";
import { UPDATE_COMMENTS_FILTER } from "contexts/toc/tocContextReducer";
import {
  AddHierarchyCommentDetails,
  AddTlfItemCommentDetails,
  ICommentReturn,
  addComment,
} from "functions/api/comments/addComment";
import {
  DeleteHierarchyCommentDetails,
  DeleteTlfItemCommentDetails,
  deleteComment,
} from "functions/api/comments/deleteComment";
import {
  ISubscriptionStatusReturn,
  handleCommentsSubscription,
  handleCommentsSubscriptionDetails,
} from "functions/api/comments/handleCommentsSubscription";
import { CommentReadDto, eCommentReviewStatus } from "functions/api/comments/interface";
import {
  UpdateHierarchyCommentDetails,
  UpdateTlfItemCommentDetails,
  updateComment,
} from "functions/api/comments/updateComment";
import { eHierarchyLevel } from "functions/api/enums";
import { IAxiosBlankReturn, IRequestStatus } from "functions/api/interface";
import { TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { isEqual } from "lodash";
import { useContext, useState } from "react";
import { Operation, createPatch } from "rfc6902";
import { SET_HIERARCHY_SUBSCRIPTION_STATUS } from "../../../contexts/hierarchy/interface";
import {
  SET_TLF_COMMENT_PANEL,
  SET_TLF_SUBSCRIPTION_STATUS,
  UPDATE_TLF,
} from "../../../contexts/tlf/interface";
import { CommentFilterOptionValue, CommentPanelStatus, CommentViewContext } from "./interface";
import { sortCommentsAscending } from "./sortComments";

export interface useCommentsReturn {
  /**
   * Active context comment panel status
   */
  commentPanelStatus: CommentPanelStatus | null;
  /**
   * Active context comments
   */
  comments: CommentReadDto[];
  /**
   * Active context current comment filter
   */
  commentFilter: CommentFilterOptionValue;
  /**
   * Create a new comment in the assigned context.
   *
   * @param parentId - Parent comment id
   * @param relatedTo - Section/Item/Id in assigned context the comment relates to
   * @returns The newly created comment or undefined when creation failed
   */
  createComment: (parentId?: string, relatedTo?: string) => CommentReadDto | undefined;
  /**
   * Delete a comment in the assigned context,
   * status of the request is in the deleteRequest variable
   * @param commentId Id of the comment to delete
   * @returns (commentId: string)
   */
  deleteComment: (commentId: string) => Promise<void>;
  /** Comment delete request status */
  deleteRequest: IRequestStatus<DeleteHierarchyCommentDetails | DeleteTlfItemCommentDetails>;
  /**
   * Current subscription status for the active comment context.
   */
  isSubscribed: boolean;
  /**
   * Read an existing comment from the assigned context
   * @param commentId Assigned comment id
   * @returns (commentId: string)
   */
  readComment: (commentId: string) => CommentReadDto | undefined;
  /**
   * Sets the comment filter in the appropriate context based on the active context.
   * @param commentFilter - The new comment filter to set.
   *                        This should be of type CommentFilterOptionValue.
   * @returns (commentFilter: CommentFilterOptionValue)
   */
  setCommentFilter: (commentFilter: CommentFilterOptionValue) => void;
  /**
   * Sets the comments in the appropriate context based on the active context.
   * @param newComments - The new comments to set in the context.
   * @returns (newComments: CommentReadDto[], sendPatch?: boolean)
   */
  setComments: (newComments: CommentReadDto[], sendPatch?: boolean) => void;
  /**
   * Sets the comment panel status in the appropriate context based on the active context.
   * @param status - The new comment panel status to set.
   *                 This should be of type CommentPanelStatus.
   */
  setCommentPanelStatus: React.Dispatch<CommentPanelStatus>;
  /**
   * Title for the comments section based on the active context.
   */
  title: string;
  /**
   * Toggle comments subscription for the active context
   * @returns ()
   */
  toggleCommentsSubscription: () => Promise<void>;
  /** Toggle comment subscription request status */
  toggleCommentsSubscriptionRequest: IRequestStatus<handleCommentsSubscriptionDetails>;
  /**
   * Post comment to the server,
   * status of the request is in the postRequest variable
   * @param commentId Id of the comment to save
   * @returns (commentId: string, newContent: IEditorV3)
   */
  updateComment: (commentId: string, newContent: IEditorV3) => Promise<void>;
  /** Comment update request status */
  updateRequest: IRequestStatus<
    | AddHierarchyCommentDetails
    | AddTlfItemCommentDetails
    | UpdateHierarchyCommentDetails
    | UpdateTlfItemCommentDetails
  >;
}

/**
 * Hook to manage comments for a specific context (Hierarchy, TlfItem, or Toc).
 *
 * @param activeContext - The context in which comments are being managed
 * @returns Object containing comment state, actions, and request statuses
 */
export const useComments = (activeContext: CommentViewContext): useCommentsReturn => {
  // Get contexts
  const signalR = useContext(SignalRContext);
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const level = hierarchyContext?.hierarchy?.level;
  const tlfContext = useContext(TlfContext);
  const { state, dispatch } = useContext(TocContext);

  // #region Variables based on context
  /**
   * Title for the comments section based on the active context.
   */
  const title = `${
    activeContext === "Hierarchy" && level
      ? level
      : activeContext === "TlfItem" &&
          tlfContext &&
          tlfContext.tlfData &&
          tlfContext.tlfData.primaryHierarchyLevel === eHierarchyLevel.Report
        ? "Shell"
        : activeContext === "TlfItem" && tlfContext?.tlfData
          ? "Template"
          : "Unknown"
  } comments`;

  /**
   * Entity ID based on the active context.
   */
  const entityId =
    activeContext === "Hierarchy" ? hierarchyContext?.hierarchy?.id : tlfContext?.tlfData?.id;

  /**
   * Check if the current context is subscribed to comments.
   */
  const isSubscribed =
    activeContext === "Hierarchy" && hierarchyContext
      ? hierarchyContext.isSubscribed
      : activeContext === "TlfItem" && tlfContext
        ? tlfContext.isSubscribed
        : false;

  /**
   * Current comment panel status based on the active context.
   */
  const commentPanelStatus =
    activeContext === "Hierarchy" && hierarchyContext
      ? hierarchyContext?.commentPanelStatus
      : activeContext === "TlfItem" && tlfContext
        ? tlfContext.commentPanelStatus
        : null;

  /**
   * Current comment filter based on the active context.
   */
  const commentFilter =
    activeContext === "Toc" ? state.commentsFilter : (commentPanelStatus?.commentFilter ?? "all");

  /**
   * Details for the active context to use in server requests.
   */
  const xdetails:
    | { id: string; azsolId: string; version: number }
    | { hierarchyId: string }
    | null =
    activeContext === "Hierarchy" && hierarchyContext?.hierarchy
      ? { hierarchyId: hierarchyContext?.hierarchy.id }
      : activeContext === "TlfItem" && tlfContext?.tlfData
        ? {
            id: tlfContext.tlfData.id,
            azsolId: tlfContext?.tlfData.azsolId,
            version: tlfContext?.tlfData.version,
          }
        : null;

  /**
   * Fetches comments from the active context and cleans up any bad parentIds.
   */
  const comments =
    activeContext === "Hierarchy" && hierarchyContext?.hierarchy?.comments
      ? hierarchyContext.hierarchy.comments
      : ["TlfItem", "Toc"].includes(activeContext) &&
          tlfContext?.tlfData &&
          "comments" in tlfContext.tlfData
        ? (tlfContext.tlfData as TlfItemReadDto).comments
        : [];
  // Clean bad parentIds
  comments.forEach((c) => {
    if (c.parentId && !comments.find((p) => p.id === c.parentId)) {
      c.parentId = undefined;
    }
  });
  // #endregion

  // #region Utility functions
  /**
   * Sets the comment panel status in the appropriate context based on the active context.
   *
   * @param status - The new comment panel status to set.
   *        This should be of type CommentPanelStatus.
   */
  const setCommentPanelStatusInContext = (status: CommentPanelStatus) => {
    if (activeContext === "Hierarchy") {
      hierarchyContext?.dispatch({
        operation: SET_HIERARCHY_COMMENT_PANEL,
        commentPanelStatus: status,
      });
    } else {
      tlfContext?.dispatch({
        operation: SET_TLF_COMMENT_PANEL,
        calledFrom: "useComments:setCommentPanelStatus",
        commentPanelStatus: status,
      });
    }
  };

  /**
   * Sets the comment filter in the appropriate context based on the active context.
   *
   * @param commentFilter - The new comment filter to set.
   *        This should be of type CommentFilterOptionValue.
   */
  const setCommentFilterInContext = (commentFilter: CommentFilterOptionValue) => {
    switch (activeContext) {
      case "Hierarchy":
      case "TlfItem":
        /* istanbul ignore else */
        if (commentPanelStatus) {
          setCommentPanelStatusInContext({
            ...commentPanelStatus,
            commentFilter: commentFilter as CommentFilterOptionValue,
          });
        }
        break;

      case "Toc":
        dispatch({
          operation: UPDATE_COMMENTS_FILTER,
          commentsFilter: commentFilter,
        });
        break;
    }
  };

  /**
   * Signal R groupname
   */
  const signalRgroup =
    activeContext === "Hierarchy"
      ? `hie${hierarchyContext?.hierarchy?.id}`
      : `tlf${tlfContext?.tlfData?.id}`;

  /**
   * Send a comment update to other users in the assigned context
   *
   * @param commentsPatch - Comments patch to send
   * @returns Promise resolving to the sent message
   */
  const sendCommentPatchToOthers = async (commentsPatch: Operation[]) => {
    return await signalR?.sendMessageToGroup(
      JSON.stringify(commentsPatch),
      signalRgroup,
      "commentsPatch",
    );
  };

  /**
   * Sets the comments in the appropriate context based on the active context.
   *
   * @param newComments - The new comments to set in the context.
   * @param sendPatch - When true, send a patch of the changes to other users.
   */
  const setCommentsInContext = (newComments: CommentReadDto[], sendPatch = false) => {
    /* istanbul ignore else */
    if (sendPatch && comments) {
      const patch = createPatch(
        comments.sort(sortCommentsAscending),
        newComments.sort(sortCommentsAscending),
      );
      /* istanbul ignore else */
      if (patch.length > 0) {
        sendCommentPatchToOthers(patch);
      }
    }
    /* istanbul ignore else */
    if (
      activeContext === "Hierarchy" &&
      hierarchyContext &&
      hierarchyContext.hierarchy &&
      !isEqual(newComments, hierarchyContext.hierarchy.comments)
    ) {
      const hierarchyPatch = { comments: newComments };
      hierarchyContext.dispatch({
        operation: UPDATE_HIERARCHY,
        hierarchy: hierarchyPatch,
      });
    } else if (
      activeContext === "TlfItem" &&
      hierarchyContext &&
      tlfContext &&
      tlfContext.tlfData &&
      "comments" in tlfContext.tlfData &&
      !isEqual(newComments, tlfContext.tlfData.comments)
    ) {
      tlfContext.dispatch({
        operation: UPDATE_TLF,
        tlfData: { comments: newComments },
        calledFrom: "useComments:setCommentsInContext",
        doNotAddUndo: true,
      });
    }
  };

  /**
   * Updates the subscription status in the appropriate context based on the active context.
   *
   * @param status - A boolean indicating the subscription status to set.
   *        `true` means subscribed, and `false` means unsubscribed.
   */
  const setSubscriptionStatusInContext = (status: boolean) => {
    if (activeContext === "Hierarchy") {
      hierarchyContext?.dispatch({
        operation: SET_HIERARCHY_SUBSCRIPTION_STATUS,
        isSubscribed: status,
      });
    } else {
      tlfContext?.dispatch({
        operation: SET_TLF_SUBSCRIPTION_STATUS,
        calledFrom: "useComments:setSubscriptionStatus",
        isSubscribed: status,
      });
    }
  };

  /**
   * Create a new comment in the assigned context
   *
   * @param parentId - Id of the parent comment, if any
   * @param relatedTo - Section/Item/Id in assigned context the comment relates to
   */
  const createComment = (parentId?: string, relatedTo?: string) => {
    /* istanbul ignore else */
    if (comments && appSettings.userId && appSettings.userId > -1) {
      const now = new Date().toISOString();
      const newComment: CommentReadDto = {
        id: `new-${crypto.randomUUID()}`,
        authorDisplayName: appSettings.displayName,
        authorId: appSettings.userId,
        commentReviewStatus: eCommentReviewStatus.open,
        content: { lines: [{ textBlocks: [{ text: "" }] }] },
        createdAt: now,
        parentId,
        relatedTo,
        updateCount: 0,
        updatedAt: now,
        canDelete: true,
      };
      setCommentsInContext([...comments, newComment]);
      return newComment;
    }
  };

  /**
   * Read an existing comment from the assigned context
   *
   * @param commentId - Assigned comment id
   */
  const readComment = (commentId: string) => comments?.find((c) => c.id === commentId);

  /**
   * Send a comment to other users in the assigned context
   *
   * @param comment - Comment to send
   * @returns Promise resolving to the sent message
   */
  const sendCommentToOthers = async (comment: CommentReadDto) => {
    return await signalR?.sendMessageToGroup(JSON.stringify(comment), signalRgroup, "comment");
  };

  /**
   * Delete a comment for other users in the assigned context
   *
   * @param commentId - Id of the comment to delete
   * @returns Promise resolving to the sent message
   */
  const deleteCommentForOthers = async (commentId: string) => {
    return await signalR?.sendMessageToGroup(
      JSON.stringify({ id: commentId }),
      signalRgroup,
      "deleteComment",
    );
  };

  // Update request status
  const [updateRequest, setUpdateRequest] = useState<
    IRequestStatus<
      | AddHierarchyCommentDetails
      | AddTlfItemCommentDetails
      | UpdateHierarchyCommentDetails
      | UpdateTlfItemCommentDetails
    >
  >({ requesting: false, error: false });
  /**
   * Post comment to the server,
   * status of the request is in the postRequest variable
   *
   * @param commentId - Id of the comment to save
   * @param commentContents - New content for the comment
   */
  const updateCommentInContext = async (commentId: string, commentContents: IEditorV3) => {
    const newComments = [...comments];
    const ix = newComments.findIndex((c) => c.id === commentId);
    // Check comment is found and can be posted
    /* istanbul ignore else */
    if (ix > -1 && xdetails) {
      const thisComment = newComments[ix];
      // Remove any errors
      setUpdateRequest({ ...updateRequest, error: false });
      // Set request
      let request: ICommentReturn | null;
      // Add new comment to the server & context
      if (thisComment.id.startsWith("new")) {
        const dto = {
          content: commentContents,
          parentId: thisComment.parentId,
          relatedTo: thisComment.relatedTo,
          ...xdetails,
        };
        request = await loadWrapper(
          addComment,
          dto,
          { ...updateRequest, requestedId: undefined },
          setUpdateRequest,
          appSettings.backEndUrl,
        );
      }
      // Update existing comment on the server
      else {
        const dto = {
          commentId: thisComment.id,
          content: commentContents,
          ...xdetails,
        };
        request = await loadWrapper(
          updateComment,
          dto,
          { ...updateRequest, requestedId: undefined } as IRequestStatus<
            UpdateHierarchyCommentDetails | UpdateTlfItemCommentDetails
          >,
          setUpdateRequest,
          appSettings.backEndUrl,
        );
      }
      // Process successful return
      /* istanbul ignore else */
      if (request?.success) {
        newComments.splice(ix, 1, request.data);
        setCommentsInContext(newComments);
        sendCommentToOthers(request.data);
        /* istanbul ignore else */
        if (!isSubscribed) setSubscriptionStatusInContext(true);
      }
    }
  };

  // Delete request status.
  const [deleteRequest, setDeleteRequest] = useState<
    IRequestStatus<DeleteHierarchyCommentDetails | DeleteTlfItemCommentDetails>
  >({ requesting: false, error: false });
  /**
   * Delete a comment in the assigned context,
   * status of the request is in the deleteRequest variable
   *
   * @param commentId - Id of the comment to delete
   */
  const deleteCommentFromContext = async (commentId: string) => {
    const newComments = [...comments];
    const ix = comments.findIndex((c) => c.id === commentId);
    /* istanbul ignore else */
    if (ix > -1 && xdetails) {
      const thisComment = newComments[ix];
      let response: IAxiosBlankReturn | null = null;
      // Send request to delete existing comment
      if (!thisComment.id.startsWith("new")) {
        response = await loadWrapper(
          deleteComment,
          { commentId, ...xdetails },
          deleteRequest,
          setDeleteRequest,
          appSettings.backEndUrl,
        );
      }
      // Assume new entries can automatically be deleted
      else {
        response = { success: true };
      }
      // Update on success
      /* istanbul ignore else */
      if (response && response.success) {
        newComments.splice(ix, 1);
        setCommentsInContext(newComments);
        deleteCommentForOthers(thisComment.id);
      }

      // Remove any highlight for deleted comment
      /* istanbul ignore else */
      if (commentPanelStatus && commentPanelStatus.highlightCommentId === commentId) {
        setCommentPanelStatusInContext({
          ...commentPanelStatus,
          highlightCommentId: "",
          activeRelatedTo: "",
        });
      }
    }
  };

  // Toggle comment subscription request status.
  const [toggleCommentsSubscriptionRequest, setToggleCommentsSubscriptionRequest] = useState<
    IRequestStatus<handleCommentsSubscriptionDetails>
  >({ requesting: false, error: false });

  /**
   * Toggle comments subscription for the active context
   */
  const toggleCommentsSubscription = async () => {
    if (!entityId || activeContext === "Toc") {
      return;
    }

    const action = isSubscribed ? "unsubscribe" : "subscribe";
    const response: ISubscriptionStatusReturn | null = await loadWrapper(
      handleCommentsSubscription,
      { activeContext, entityId, action },
      toggleCommentsSubscriptionRequest,
      setToggleCommentsSubscriptionRequest,
      appSettings.backEndUrl,
    );

    /* istanbul ignore else */
    if (response?.success) {
      setSubscriptionStatusInContext(response.data);
    }
  };
  // #endregion

  return {
    commentFilter,
    commentPanelStatus,
    comments,
    createComment,
    deleteComment: deleteCommentFromContext,
    deleteRequest,
    isSubscribed,
    readComment,
    setCommentFilter: setCommentFilterInContext,
    setCommentPanelStatus: setCommentPanelStatusInContext,
    setComments: setCommentsInContext,
    title,
    toggleCommentsSubscription,
    toggleCommentsSubscriptionRequest,
    updateComment: updateCommentInContext,
    updateRequest,
  };
};
